import { useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ApiError } from '@quicktl/api-client';

type TokenState = 'checking' | 'valid' | 'missing' | 'invalid';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [tokenState, setTokenState] = useState<TokenState>('checking');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenState('missing');
      return;
    }
    let cancelled = false;
    api.validatePasswordResetToken({ token })
      .then(() => { if (!cancelled) setTokenState('valid'); })
      .catch(() => { if (!cancelled) setTokenState('invalid'); });
    return () => { cancelled = true; };
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.confirmPasswordReset({ token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(body?.error ?? 'Failed to reset password. The link may have expired.');
        // Treat any 400 as token-level failure so the user sees the full error UI.
        if (err.status === 400) setTokenState('invalid');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold tracking-tight"><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">QuickTL</span></h1>
          <p className="mt-1 text-sm text-slate-400">Choose a new password</p>
        </div>

        {tokenState === 'checking' && (
          <div className="rounded-md bg-slate-900 border border-slate-800 px-4 py-6 text-center text-sm text-slate-400">
            Verifying your reset link…
          </div>
        )}

        {(tokenState === 'missing' || tokenState === 'invalid') && (
          <div className="space-y-4">
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-4 text-sm text-red-400">
              {tokenState === 'missing'
                ? 'This page requires a reset token. Please use the link from your email.'
                : 'This reset link is invalid, has expired, or has already been used.'}
            </div>
            <Link
              to="/forgot-password"
              className="block w-full text-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 transition-colors"
            >
              Request a new reset link
            </Link>
          </div>
        )}

        {tokenState === 'valid' && success && (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 px-4 py-4 text-sm text-emerald-300">
            Password reset successfully. Redirecting you to sign in…
          </div>
        )}

        {tokenState === 'valid' && !success && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div role="alert" className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">
                New password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="At least 12 characters"
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-300 mb-1">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-slate-400">
          <Link to="/login" className="text-emerald-400 hover:text-emerald-300">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
